import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

const getAuthHeaders = (token) => ({
    headers: { Authorization: `Bearer ${token}` }
});

/**
 * Gets available F.R.A.N.C.A. AI models and service availability
 * @param {string} token - The user's authentication token
 * @returns {Promise<object>} - Available models and service status
 */
export const getFrancaModels = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/generate/franca/models`,
        getAuthHeaders(token)
    );
    return response.data;
};

// Legacy function name for backward compatibility
export const getGeminiModels = getFrancaModels;

/**
 * Generates an image from text prompt using F.R.A.N.C.A. AI
 * @param {string} token - The user's authentication token
 * @param {string} prompt - The text prompt for image generation
 * @param {string} model - The F.R.A.N.C.A. AI model to use (optional, defaults to advanced)
 * @returns {Promise<object>} - Response with generated images array
 */
export const generateImageFromText = async (token, prompt, model = 'advanced') => {
    const response = await axios.post(
        `${API_BASE_URL}/generate/franca/image`,
        { prompt, model },
        getAuthHeaders(token)
    );
    return response.data;
};

/**
 * Generates multiple in-chat explanatory visuals for article/book-style answers.
 * @param {string} token - The user's authentication token.
 * @param {Array<{section_title: string, visual_prompt: string, caption?: string, model?: string}>} items
 * @returns {Promise<object>} - Response with generated section visuals.
 */
export const generateExplanationVisuals = async (token, items) => {
    const response = await axios.post(
        `${API_BASE_URL}/generate/franca/explanation-visuals`,
        { items },
        getAuthHeaders(token)
    );
    return response.data;
};

/**
 * Edits an image based on text prompt using F.R.A.N.C.A. AI
 * @param {string} token - The user's authentication token
 * @param {string} prompt - The text prompt describing the edit
 * @param {File} imageFile - The image file to edit
 * @param {string} model - The F.R.A.N.C.A. AI model to use (optional)
 * @returns {Promise<object>} - Response with edited images array
 */
export const editImageWithText = async (token, prompt, imageFile, model) => {
    const formData = new FormData();
    formData.append('prompt', prompt);
    formData.append('image', imageFile);
    if (model) {
        formData.append('model', model);
    }

    const response = await axios.post(
        `${API_BASE_URL}/generate/franca/edit`,
        formData,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        }
    );
    return response.data;
};

/**
 * Generates an image from multiple input images using F.R.A.N.C.A. AI
 * @param {string} token - The user's authentication token
 * @param {string} prompt - The text prompt for image generation
 * @param {File[]} imageFiles - Array of image files (up to 3)
 * @param {string} model - The F.R.A.N.C.A. AI model to use (optional)
 * @returns {Promise<object>} - Response with generated images array
 */
export const generateFromMultipleImages = async (token, prompt, imageFiles, model) => {
    if (imageFiles.length > 3) {
        throw new Error('Maximum 3 images allowed');
    }

    const formData = new FormData();
    formData.append('prompt', prompt);
    imageFiles.forEach((file, index) => {
        formData.append('images', file);
    });
    if (model) {
        formData.append('model', model);
    }

    const response = await axios.post(
        `${API_BASE_URL}/generate/franca/multi-image`,
        formData,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        }
    );
    return response.data;
};

/**
 * Generates image with interleaved text and images using F.R.A.N.C.A. AI
 * @param {string} token - The user's authentication token
 * @param {Array} content - Array of content items with type 'text' or 'image'
 * @param {string} model - The F.R.A.N.C.A. AI model to use (optional)
 * @returns {Promise<object>} - Response with generated images array
 */
export const generateInterleavedContent = async (token, content, model) => {
    const formData = new FormData();
    
    // Add content as JSON string
    formData.append('content', JSON.stringify(content));
    
    // Add image files referenced in content
    content.forEach((item, index) => {
        if (item.type === 'image' && item.file) {
            formData.append(`image_${index}`, item.file);
        }
    });
    
    if (model) {
        formData.append('model', model);
    }

    const response = await axios.post(
        `${API_BASE_URL}/generate/franca/interleaved`,
        formData,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        }
    );
    return response.data;
};

/**
 * Helper function to convert base64 string to File object
 * @param {string} base64String - Base64 encoded image data
 * @param {string} filename - Name for the file
 * @param {string} mimeType - MIME type of the image
 * @returns {File} - File object
 */
export const base64ToFile = (base64String, filename = 'image.png', mimeType = 'image/png') => {
    // Remove data URL prefix if present
    const base64Data = base64String.replace(/^data:image\/[a-z]+;base64,/, '');
    
    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    
    return new File([byteArray], filename, { type: mimeType });
};

/**
 * Helper function to validate image file
 * @param {File} file - File to validate
 * @returns {boolean} - True if valid image file
 */
export const isValidImageFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    return validTypes.includes(file.type) && file.size <= maxSize;
};

/**
 * Helper function to resize image file if needed
 * @param {File} file - Image file to resize
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<File>} - Resized image file
 */
export const resizeImageFile = (file, maxWidth = 1024, maxHeight = 1024, quality = 0.9) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            // Calculate new dimensions
            let { width, height } = img;
            if (width > maxWidth || height > maxHeight) {
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                width *= ratio;
                height *= ratio;
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Draw and compress
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
                (blob) => {
                    const resizedFile = new File([blob], file.name, {
                        type: file.type,
                        lastModified: Date.now()
                    });
                    resolve(resizedFile);
                },
                file.type,
                quality
            );
        };
        
        img.src = URL.createObjectURL(file);
    });
};

/**
 * Generates an enhanced "magic" prompt from a simple user idea using F.R.A.N.C.A. AI
 * @param {string} token - The user's authentication token
 * @param {string} simplePrompt - The user's basic prompt text
 * @param {string} length - The desired length of the enhanced prompt ("short" | "medium" | "long"). Defaults to "medium".
 * @returns {Promise<string>} - The enhanced prompt text
 */
export const getMagicPrompt = async (token, simplePrompt, length = 'medium') => {
    const response = await axios.post(
        `${API_BASE_URL}/generate/magic-prompt`,
        { simple_prompt: simplePrompt, length: length },
        getAuthHeaders(token)
    );
    return response.data.magic_prompt;
};

/**
 * Edits an image using magic prompt enhancement in a single operation
 * @param {string} token - The user's authentication token
 * @param {string} simplePrompt - The user's simple editing prompt
 * @param {File} imageFile - The image file to edit
 * @param {string} model - The F.R.A.N.C.A. AI model to use (optional)
 * @param {string} length - The desired length of the enhanced prompt ("short" | "medium" | "long"). Defaults to "medium".
 * @returns {Promise<object>} - Response with edited images and metadata
 */
export const magicEditImage = async (token, simplePrompt, imageFile, model = 'advanced', length = 'medium') => {
    try {
        // Try the new magic-edit endpoint first
        const formData = new FormData();
        formData.append('simple_prompt', simplePrompt);
        formData.append('image', imageFile);
        formData.append('model', model);
        formData.append('length', length);

        const response = await axios.post(
            `${API_BASE_URL}/generate/franca/magic-edit`,
            formData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response.data;
    } catch (error) {
        // If magic-edit endpoint doesn't exist (404), fall back to two-step process
        if (error.response?.status === 404) {
            console.warn('Magic-edit endpoint not available, falling back to two-step process');
            
            try {
                // Step 1: Enhance the prompt
                const magicPromptResponse = await getMagicPrompt(token, simplePrompt, length);
                const enhancedPrompt = magicPromptResponse;
                
                // Step 2: Edit the image with enhanced prompt
                const editResponse = await editImageWithText(token, enhancedPrompt, imageFile, model);
                
                // Return with additional metadata
                return {
                    ...editResponse,
                    metadata: {
                        ...editResponse.metadata,
                        magic_prompt: {
                            original_prompt: simplePrompt,
                            enhanced_prompt: enhancedPrompt,
                            enhancement_applied: true,
                            fallback_used: true
                        }
                    }
                };
            } catch (fallbackError) {
                // If everything fails, just use regular edit with original prompt
                console.warn('Magic prompt enhancement failed, using original prompt');
                return await editImageWithText(token, simplePrompt, imageFile, model);
            }
        }
        
        // For other errors, re-throw
        throw error;
    }
};

/**
 * Fetches user's image generation history
 * @param {string} token - The user's authentication token
 * @param {number} limit - Maximum number of items to return
 * @param {number} sinceMinutes - Filter history from the last N minutes
 * @returns {Promise<Array>} - Array of image generation history items
 */
export const getImageHistory = async (token, limit = 5, sinceMinutes = 60) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (sinceMinutes) params.append('since_minutes', sinceMinutes.toString());
    
    const response = await axios.get(
        `${API_BASE_URL}/generate/history/images?${params.toString()}`,
        getAuthHeaders(token)
    );
    return response.data;
};

export const getMediaInfo = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/generate/media/info`,
        getAuthHeaders(token)
    );
    return response.data;
};

export const listMediaAssets = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/generate/media/list`,
        getAuthHeaders(token)
    );
    return response.data;
};

export const deleteSingleMedia = async (token, filename) => {
    const response = await axios.delete(
        `${API_BASE_URL}/generate/media/${encodeURIComponent(filename)}`,
        getAuthHeaders(token)
    );
    return response.data;
};

export const clearAllGeneratedMedia = async (token) => {
    const response = await axios.delete(
        `${API_BASE_URL}/generate/media/clear`,
        getAuthHeaders(token)
    );
    return response.data;
};